import { useWindowSize } from '@uidotdev/usehooks'
import { clsx } from 'clsx'
import type { ReactNode } from 'react'
import React from 'react'
import { DatoImage } from '#src/components/image/DatoImage.js'
import { LocalizedLink } from '#src/components/LocalizedLink.js'
import { VimeoPlayer } from '#src/components/VimeoPlayer.js'
import type { GridSingleCell, ImageGallery, VideoData, VideoGallery } from '#src/data/data.js'
import { isImageGallery, isVideoGallery } from '#src/dataService.js'

interface Props {
  cell: GridSingleCell
  galleryPath: string
  className?: string
}

export function GridSingleCellComponent(props: Props): ReactNode {
  const { cell, galleryPath, className } = props
  const { gallery } = props.cell

  const windowSize = useWindowSize()
  const windowWidth = windowSize.width || 0
  const isMobile = windowWidth <= 600

  let video: VideoData | undefined
  if (isVideoGallery(gallery)) {
    video = gallery.video
  } else if (isImageGallery(gallery) && gallery.videoCover) {
    video = gallery.videoCover
  }

  const horizontal =
    (gallery as ImageGallery).cover?.tags?.includes('horizontal') ||
    (gallery as VideoGallery).horizontal

  // Workaround DatoCMS bug where it assigns 'Center' instead of 'center'
  if ((cell.alignment as any) === 'Center') delete cell.alignment

  return (
    <LocalizedLink
      className={clsx({
        grid__cell: true,
        grid__cell_horizontal: horizontal,
        grid__cell_left: gallery.left,
        grid__cell_right: !horizontal && gallery.right,
        grid__cell_landscape: !!cell.landscape,
        [`grid__cell_alignment_${cell.alignment}`]: cell.alignment,
        [className!]: !isMobile && className,
      })}
      style={{
        width: isMobile ? undefined : `${cell.width || 100}%`,
      }}
      href={`${galleryPath}${gallery.slug}`}
      scroll={false}
    >
      {video ? (
        <VimeoPlayer video={video} className="grid__image" />
      ) : (
        isImageGallery(gallery) &&
        gallery.cover && (
          <DatoImage
            data={gallery.cover.responsiveImage}
            containerSize={isMobile ? 80 : 45}
            style={{ maxWidth: '100%' }}
          />
        )
      )}

      <div className="grid__undertext">
        <p className="grid__title">{gallery.client}</p>
        <p className="grid__artist">{gallery.artist}</p>
      </div>
    </LocalizedLink>
  )
}
